// Componente que destaca o botão "Imprimir" enquanto etapa PDF está pendente
// Desaparece automaticamente após conclusão

import { useEffect, useState } from "react";
import { useAuth } from "@/components/AuthProvider";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getOnboardingProgress, completeOnboardingStep } from "@/integrations/supabase/onboarding";
import { ONBOARDING_STEPS } from "@/constants/onboarding";
import { toast } from "sonner";

interface OnboardingPDFHighlightProps {
  isOpen: boolean;
  onPDFViewed?: () => void;
}

/**
 * Hook customizado para marcar a etapa de visualização de PDF como concluída
 * Use dentro do componente que renderiza o PDF (ex: VisualizarOrcamento)
 */
export function useOnboardingPDFDetection() {
  const { empresa } = useAuth();
  const queryClient = useQueryClient();
  const [hasMarkedPDF, setHasMarkedPDF] = useState(false);

  const { data: onboardingProgress } = useQuery({
    queryKey: ["onboarding-progress", empresa?.id],
    queryFn: async () => {
      if (!empresa?.id) return null;
      const response = await getOnboardingProgress(empresa.id);
      return response;
    },
    enabled: !!empresa?.id,
  });

  // Marcar PDF como visualizado quando a etapa for atingida
  useEffect(() => {
    if (!empresa?.id || hasMarkedPDF) return;

    const isPDFPending =
      onboardingProgress &&
      onboardingProgress.shouldShowChecklist &&
      onboardingProgress.steps?.some(
        s => s.step === ONBOARDING_STEPS.QUOTE_PDF_VIEWED && !s.completed
      );

    if (isPDFPending) {
      (async () => {
        await completeOnboardingStep(empresa.id, ONBOARDING_STEPS.QUOTE_PDF_VIEWED);
        queryClient.invalidateQueries({ queryKey: ['onboarding-progress', empresa.id] });
        toast.success("✅ PDF visualizado! Etapa 4 concluída!");
        setHasMarkedPDF(true);
      })();
    }
  }, [empresa?.id, onboardingProgress, hasMarkedPDF, queryClient]);

  return {
    isPDFPending:
      onboardingProgress?.steps?.some(
        s => s.step === ONBOARDING_STEPS.QUOTE_PDF_VIEWED && !s.completed
      ) || false,
  };
}

/**
 * Componente de destaque visual para o botão "Imprimir"
 * Mostra uma animação discreta enquanto o step de PDF está pendente
 */
export function OnboardingPDFHighlight() {
  const { empresa } = useAuth();
  const [shouldHighlight, setShouldHighlight] = useState(false);

  const { data: onboardingProgress } = useQuery({
    queryKey: ["onboarding-progress", empresa?.id],
    queryFn: () => getOnboardingProgress(empresa?.id!),
    enabled: !!empresa?.id,
  });

  useEffect(() => {
    if (!onboardingProgress) return;

    // Mostrar destaque apenas se:
    // 1. Está em progresso (não concluído)
    // 2. PDF ainda não foi visualizado
    const isPDFPending =
      onboardingProgress.shouldShowChecklist &&
      onboardingProgress.steps?.some(
        s => s.step === ONBOARDING_STEPS.QUOTE_PDF_VIEWED && !s.completed
      );

    setShouldHighlight(!!isPDFPending);
  }, [onboardingProgress]);

  // Retorna apenas um hook, não um elemento visual
  // O elemento visual é controlado via className CSS
  return shouldHighlight;
}

// Classe CSS para aplicar ao botão "Imprimir"
// Use assim: <Button className={onboardingPDFVisible ? "animate-pulse border-2 border-purple-500 shadow-lg shadow-purple-300" : ""}>
export const PDF_HIGHLIGHT_CLASS = "animate-pulse border-2 border-purple-500 shadow-lg shadow-purple-300 ring-2 ring-purple-200";
